// Composes the section nav and glossary rail. Reads the two hooks for the
// two-level filter (section in view → viewport-visible blocks within).
// Per 01_renderer.md: seed terms (origin "openapi" / "user") are
// section-agnostic — they appear in every section as long as they pass the
// viewport filter.

import { useMemo } from "react";
import type { DocJSON, PublishedGlossary, TermOrigin } from "../data/types";
import type { CanonicalResolver } from "./canonicalResolver";
import { GlossaryRow } from "./GlossaryRow";
import { resolveTermFamily } from "../reading/termFamily";
import { disclose, ORIGIN_ATTR, ORIGIN_LEGEND_ORDER } from "../reading/termOrigin";

const LEGEND_FAMILIES = ["foundational", "specialized", "scenario"] as const;

export interface RailProps {
  doc: DocJSON;
  glossary: PublishedGlossary;
  activeSectionId: string | null;
  activeTerms: ReadonlySet<string> | null;
  canonicalResolver?: CanonicalResolver;
  // Optional. When provided, section-nav entries render as <button> and
  // call back with the section id on click — ReadingPage attaches the
  // goTo + ResizeObserver re-anchor here. When absent, entries render as
  // plain <a href="#id"> for back-compat with slice-1 fixtures that don't
  // wire goTo.
  onSectionSelect?: (id: string) => void;
}

export function Rail({
  doc, glossary, activeSectionId, activeTerms, canonicalResolver,
  onSectionSelect,
}: RailProps) {
  // Build a lookup from term -> set of block ids that hold this term (best
  // effort: source_block_id from extraction). Seed terms have no
  // source_block_id and pass the section filter unconditionally.
  const activeSection = useMemo(
    () => doc.sections.find((s) => s.id === activeSectionId) ?? null,
    [doc, activeSectionId],
  );

  // PRD 09 — lookup for the "Built from:" disclosure in compound rail
  // rows. Stable across renders unless the glossary changes.
  const termsById = useMemo(() => {
    const m = new Map<string, (typeof glossary.terms)[number]>();
    for (const t of glossary.terms) m.set(t.term_id, t);
    return m;
  }, [glossary.terms]);

  const visibleTerms = useMemo(() => {
    return glossary.terms.filter((t) => {
      const isSeed = t.origin === "openapi" || t.origin === "user";
      const sectionMatch =
        isSeed ||
        !activeSectionId ||
        (t.source_block_id != null &&
          activeSection?.blocks.some((b) => b.id === t.source_block_id)) === true;
      if (!sectionMatch) return false;
      // `null` activeTerms means the observer hasn't fired yet — surface
      // everything that passes the section filter rather than nothing.
      const viewportMatch = activeTerms === null || activeTerms.has(t.term);
      return viewportMatch;
    });
  }, [glossary, activeSection, activeSectionId, activeTerms]);

  // Adaptive family legend — same shape as widget rail.ts:renderFamilyLegend.
  // Renders only families present in the currently-visible term set; hidden
  // entirely when <2 families are visible.
  const activeFamilies = useMemo(() => {
    const s = new Set<string>();
    for (const t of visibleTerms) s.add(resolveTermFamily(t));
    return s;
  }, [visibleTerms]);

  // Adaptive origin legend — same hide-when-uniform rule as the family
  // legend. Hidden when only one origin is in the visible set (would
  // disclose nothing the per-row pill doesn't already say).
  const activeOrigins = useMemo(() => {
    const s = new Set<TermOrigin>();
    for (const t of visibleTerms) s.add(t.origin);
    return s;
  }, [visibleTerms]);

  return (
    <aside className="maisight-rail">
      {doc.sections.length ? (
        <div className="maisight-rail-head">Contents</div>
      ) : null}
      <nav className="maisight-rail-nav" aria-label="Sections">
        {doc.sections.map((s) => {
          const cls = `maisight-rail-nav-item${
            activeSectionId === s.id ? " is-active" : ""
          }`;
          const label = s.heading ?? s.id;
          // Buttons (not anchors) when onSectionSelect is wired, so the
          // browser's hash-nav doesn't race the goTo's smooth scroll +
          // ResizeObserver re-anchor.
          return onSectionSelect ? (
            <button
              key={s.id}
              type="button"
              className={cls}
              onClick={() => onSectionSelect(s.id)}
            >
              {label}
            </button>
          ) : (
            <a key={s.id} href={`#${s.id}`} className={cls}>
              {label}
            </a>
          );
        })}
      </nav>
      <div className="maisight-rail-head">
        Glossary
        {activeSectionId ? (
          <span className="maisight-rail-tag">{activeSectionId}</span>
        ) : null}
      </div>
      {/* Keyed on activeSectionId ONLY (not activeTerms). Remounts the
          body on every section crossing → re-fires the
          @keyframes maisight-glossary-fade. Intra-section viewport
          changes still drive per-row .is-active max-height transitions.
          Origin reference: docs/origin-reference/coursework-mechanic.tsx §3. */}
      <div key={activeSectionId ?? "_none"} className="maisight-glossary-body">
        {activeFamilies.size >= 2 ? (
          <div className="maisight-family-legend" aria-label="Term family legend">
            {LEGEND_FAMILIES.filter((f) => activeFamilies.has(f)).map((f) => (
              <span
                key={f}
                className="maisight-family-legend-item"
                data-term-family={f}
              >
                <span className="maisight-family-legend-dot" aria-hidden="true" />
                <span className="maisight-family-legend-label">{f}</span>
              </span>
            ))}
          </div>
        ) : null}
        {activeOrigins.size >= 2 ? (
          <div className="maisight-origin-legend" aria-label="Term origin legend">
            {ORIGIN_LEGEND_ORDER.filter((o) => activeOrigins.has(o)).map((o) => {
              const d = disclose(o);
              return (
                <span
                  key={o}
                  className="maisight-origin-legend-item"
                  {...{ [ORIGIN_ATTR]: o }}
                  title={d.longLabel}
                >
                  <span className="maisight-origin-pill" {...{ [ORIGIN_ATTR]: o }}>
                    {d.pillLabel}
                  </span>
                  <span className="maisight-origin-legend-label">{d.footerLabel}</span>
                </span>
              );
            })}
          </div>
        ) : null}
        <dl className="maisight-glossary-list">
          {glossary.terms.map((entry) => (
            <GlossaryRow
              key={entry.term_id}
              entry={entry}
              active={visibleTerms.includes(entry)}
              canonical={
                canonicalResolver?.byRef(doc.doc_id, entry.term_id) ?? null
              }
              termsById={termsById}
            />
          ))}
        </dl>
      </div>
    </aside>
  );
}
